import React, { useState, useContext, useRef, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { size } from "lodash";
import Swal from "sweetalert2";
import {
  CardHeader,
  CardContent,
  Card,
  Icon,
  Button,
  Popup,
  Confirm,
} from "semantic-ui-react";
import {
  Sev01,
  Sev02,
  Sev03,
  Smu01,
  Smu02,
  Smu03,
  Smu04,
  Smu05,
  Smu06,
  Smu07,
  Smu08,
} from "../../TemplateBar/TemplatesManager";
import { Session } from "../../../../../../api";
import { useAuth } from "../../../../../../hooks";
import { SlidesContextEdit } from "../../../../../../contexts/MediaContext";
import "./DragZone.scss";

const sessionController = new Session();

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  },
});

export function DragZone(props) {
  const { session, setActiveSessionEdit, onDropTemplate, onDragOver } = props;
  const { slidesActiveSessionEdit, setSlidesActiveSessionEdit } =
    useContext(SlidesContextEdit);
  const { accessToken } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Configures sensors for drag-and-drop functionality in `DragZone.js`.
   *
   * This code snippet is part of the setup for enabling drag-and-drop interactions within a component, utilizing the `useSensors` and `useSensor` hooks from a drag-and-drop library (likely `@dnd-kit/core`). It specifically configures a `PointerSensor` to detect drag operations based on pointer events (e.g., mouse, touch).
   *
   * Configuration Details:
   * - **PointerSensor**: This sensor is responsible for initiating drag operations based on user pointer interactions. It's a common choice for enabling drag-and-drop with mouse or touch inputs.
   * - **activationConstraint**: Defines conditions under which the drag operation should be activated.
   *   - `delay`: Specifies a delay (in milliseconds) before the drag operation begins. Here, it's set to 3000ms (3 seconds), meaning the user must press and hold the pointer for 3 seconds before dragging starts. This can help prevent accidental drags.
   *   - `tolerance`: Sets a movement tolerance (in pixels) that the pointer must move before the drag operation is considered to start. A value of 100 pixels is used, requiring significant movement to initiate the drag. This further reduces the likelihood of unintentional drags.
   *
   * This setup is crucial for implementing custom drag-and-drop behavior, allowing for fine-tuned control over how and when drag operations are triggered, enhancing the user experience by preventing accidental drags.
   */
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 3000,
        tolerance: 100,
      },
    })
  );

  /**
   * Handles the end of a drag operation by reordering slides.
   *
   * This function is triggered when a drag operation ends. It checks if the dragged item (`active`) was dropped over a different target (`over`).
   * If so, it finds the indexes of the active and over items in the `slidesActiveSessionEdit` array using their `_id` properties.
   *
   * Using the `arrayMove` utility, it then creates a new array with the slides reordered according to the drag-and-drop operation.
   * This reordered array is set as the new state for `slidesActiveSessionEdit`, effectively updating the order of slides in the active session.
   *
   * @param {Object} event - The event object containing details about the drag operation, including the `active` and `over` items.
   */
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const activeIndex = slidesActiveSessionEdit.findIndex(
        (slide) => slide._id === active.id
      );
      const overIndex = slidesActiveSessionEdit.findIndex(
        (slide) => slide._id === over.id
      );
      const newOrderSlidesActiveSession = arrayMove(
        slidesActiveSessionEdit,
        activeIndex,
        overIndex
      );
      setSlidesActiveSessionEdit(newOrderSlidesActiveSession);
    }
  };

  /**
   * Asynchronously saves the current state of slides for a session and provides user feedback.
   *
   * This function, `onSaveSlidesSession`, is part of `DragZone.js` and is responsible for saving the edited slides of a session. It interacts with a session controller to persist changes to the session's slides.
   *
   * Process:
   * 1. **Start Loading**: Sets a loading state to true, indicating that the save operation is in progress. This can be used to trigger UI feedback such as a spinner.
   * 2. **Save Operation**: Makes an asynchronous call to `sessionController.saveSlidesSession`, passing the necessary parameters including the access token, session ID, and the current state of slides being edited (`slidesActiveSessionEdit`).
   * 3. **Update State**: Upon successful save, updates the local state `setSlidesActiveSessionEdit` with the response data, which likely includes the updated slides information.
   * 4. **Success Feedback**: Displays a success message using a toast notification, indicating that the session's content has been successfully saved. The message includes the session's title for clarity.
   * 5. **Error Handling**: Catches and logs any errors that occur during the save operation. This is crucial for debugging and ensuring the user is aware of any issues.
   * 6. **End Loading**: Resets the loading state to false, signaling that the save operation has completed. This can be used to remove UI feedback indicating the operation is in progress.
   */
  const onSaveSlidesSession = async () => {
    try {
      setLoading(true);
      const response = await sessionController.saveSlidesSession(
        accessToken,
        session.id,
        slidesActiveSessionEdit
      );
      setSlidesActiveSessionEdit(response.data);
      Toast.fire({
        icon: "success",
        title: `Se guardó con éxito el contenido de la sesión ${session.title}.`,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Closes the session editing mode and notifies the user.
   *
   * This function, `onCloseSession`, is part of `DragZone.js` and is designed to handle the closure of the session editing interface. It resets the session being edited to an empty state, displays a notification to inform the user that the editing session has been closed, and triggers any necessary cleanup actions.
   *
   * Steps:
   * 1. **Reset Editing Session**: Sets the active session being edited to an empty object, effectively clearing any selected session data from the editing interface.
   * 2. **User Notification**: Utilizes `Toast.fire` to display an informational message to the user, indicating that the session editing has been closed. This feedback is crucial for a good user experience, ensuring users are aware of the change in state.
   * 3. **Cleanup Actions**: Calls `onOpenCloseConfirm` to perform any additional cleanup actions required after closing the session edit. This could involve closing modal dialogs or resetting form states.
   *
   * This function is essential for managing the user flow in the session editing feature, ensuring that users can easily exit the editing mode and are clearly informed when the editing session has ended.
   */
  const onCloseSession = () => {
    setActiveSessionEdit({});
    Toast.fire({
      icon: "info",
      title: "Se cerró la edición de la sesión.",
    });
    onOpenCloseConfirm();
  };

  /**
   * Removes a slide from the active session's slides based on its index and displays a success message.
   *
   * This function is designed to remove a specific slide from the `slidesActiveSessionEdit` array, which holds the slides for the session
   * currently being edited. It filters out the slide at the specified `slideIndex`, creating a new array without that slide. This new array
   * is then set as the updated list of slides for the active session.
   *
   * After successfully removing the slide, it displays a success message using `Toast.fire`, indicating which slide number was removed.
   *
   * @param {number} slideIndex - The index of the slide to be removed from the active session's slides.
   */
  const onRemoveSlideByIndex = (slideIndex) => {
    const newSlides = slidesActiveSessionEdit.filter(
      (slide, index) => index !== slideIndex
    );
    setSlidesActiveSessionEdit(newSlides);
    Toast.fire({
      icon: "success",
      title: `Se eliminió la hoja ${slideIndex + 1} con éxito.`,
    });
  };

  /**
   * Converts a numerical order into a lowercase letter.
   *
   * This function takes a numerical order as its argument and returns the corresponding
   * lowercase letter of the English alphabet. If the order is less than 1 or greater than 26,
   * it returns null, indicating an invalid input. The function calculates the ASCII code of the
   * letter by adding the order to 96 (the ASCII code for 'a' is 97). It then converts this ASCII
   * code back to a character using String.fromCharCode.
   *
   * @param {number} order - The numerical order of the letter in the alphabet (1 for 'a', 2 for 'b', etc.).
   * @returns {string|null} The corresponding lowercase letter of the alphabet, or null if the input is out of range.
   */
  const onLetterByOrder = (order) => {
    if (order < 1 || order > 26) return null;
    return String.fromCharCode(96 + order);
  };

  /**
   * Defines a `SlidesSessionContainer` functional component for managing a draggable session list in `DragZone.js`.
   *
   * This component utilizes the `useDroppable` hook from a drag-and-drop library to create a droppable area where slides can be reordered through drag-and-drop interactions. It is designed to work within the context of a session, identified by `session.id`.
   *
   * Key Features:
   * - **Droppable Area Setup**: Uses `useDroppable` to mark the component as a droppable area, allowing slides to be dragged into and within it. The `id` of the session is used to uniquely identify this droppable area.
   * - **DOM and Droppable Refs Management**: Manages both the droppable reference (for drag-and-drop functionality) and the DOM reference (for potential direct DOM manipulations or measurements) using a single callback function `setRefs`.
   *
   * `setRefs` Function:
   * - A callback function created with `useCallback` to ensure it is memoized and not recreated on every render.
   * - Receives a DOM node as an argument and performs two actions:
   *   1. Calls `setNodeRef` with the node to establish it as the droppable area for the drag-and-drop library.
   *   2. Sets the `sessionListRef.current` to the same node, storing a direct reference to the DOM element for other uses.
   *
   * Rendering:
   * - Renders a `div` element that serves as the container for the session's slides. This `div` is marked as the droppable area by passing the `setRefs` function to its `ref` prop.
   * - Iterates over `slidesActiveSessionEdit`, an array of slide objects for the current session, rendering each slide using the `SortableSlide` component. Each slide is passed its `index` and the slide data.
   *
   * @returns A `div` element that wraps the sortable slides of a session, equipped with drag-and-drop capabilities.
   */
  const SlidesSessionContainer = () => {
    const { setNodeRef } = useDroppable({ id: `session-${session.id}` });

    const sessionListRef = useRef(null);

    // Función para establecer ambas referencias
    const setRefs = useCallback(
      (node) => {
        // Establece la referencia del droppable
        setNodeRef(node);
        // Establece la referencia del elemento DOM
        sessionListRef.current = node;
      },
      [setNodeRef]
    );

    return (
      <div ref={setRefs} className="drag-zone__session-list">
        {slidesActiveSessionEdit.map((slide, index) => (
          <SortableSlide key={index} index={index} slide={slide} />
        ))}
      </div>
    );
  };

  /**
   * Renders a sortable slide component within a drag-and-drop context.
   *
   * Utilizes the `useSortable` hook from the `dnd-kit` library to enable drag-and-drop functionality for individual slides, identified by their unique `_id`.
   * The component applies dynamic styling based on the drag state, elevating the `zIndex` of a dragging slide and applying a CSS transform to indicate movement.
   *
   * The `renderSlide` function is invoked to display the slide's content, with the slide data and its index as parameters.
   *
   * @param {Object} props - The component props.
   * @param {number} props.index - The index of the slide within the collection.
   * @param {Object} props.slide - The slide data object, containing at least an `_id` property.
   *
   * @returns A `div` element styled and equipped with attributes and event listeners for drag-and-drop interactions, showcasing the slide's content.
   */
  const SortableSlide = ({ index, slide }) => {
    const {
      setNodeRef,
      attributes,
      listeners,
      transition,
      transform,
      isDragging,
    } = useSortable({ id: slide._id });

    const style = {
      transform: `${CSS.Transform.toString(transform)}`, //${/* isDragging ? "scale(1.02)": "scale(1)" */}
      transition,
      zIndex: isDragging ? 2 : 1,
    };

    return (
      <div
        className="drag-zone__session-slide"
        style={style}
        ref={setNodeRef}
        {...attributes}
        {...listeners}
      >
        {renderSlide(slide, index)}
      </div>
    );
  };

  /**
   * Renders a slide based on its template type.
   *
   * This function takes a slide object and its index in the array as arguments.
   * It then checks the template type of the slide and renders the corresponding
   * component. Each slide component is passed a set of common props including
   * a unique key (index), the slide index, the slide ID, and functions for removing
   * the slide by index and handling letter ordering.
   *
   * @param {Object} slide - The slide object containing information about the slide to be rendered.
   * @param {number} index - The index of the slide in the array of slides.
   * @returns A slide component based on the template type of the slide or null if the template type is unrecognized.
   *
   * Supported slide template types and their corresponding components:
   * - Sev01: Renders <Sev01 /> component.
   * - Sev02: Renders <Sev02 /> component.
   * - Sev03: Renders <Sev03 /> component.
   * - Smu01 to Smu08: Renders <Smu01 /> to <Smu08 /> components respectively.
   *
   * Each component is rendered with the following props:
   * - key: A unique key for React's rendering algorithm, set to the slide's index.
   * - index: The index of the slide.
   * - slideId: The unique identifier of the slide.
   * - onRemoveSlideByIndex: A function to remove the slide by its index.
   * - onLetterByOrder: A function to handle letter ordering.
   */
  const renderSlide = (slide, index) => {
    switch (slide.template) {
      case "Sev01":
        return (
          <Sev01
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Sev02":
        return (
          <Sev02
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Sev03":
        return (
          <Sev03
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu01":
        return (
          <Smu01
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu02":
        return (
          <Smu02
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu03":
        return (
          <Smu03
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu04":
        return (
          <Smu04
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu05":
        return (
          <Smu05
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu06":
        return (
          <Smu06
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu07":
        return (
          <Smu07
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      case "Smu08":
        return (
          <Smu08
            key={index}
            index={index}
            slideId={slide._id}
            onRemoveSlideByIndex={() => onRemoveSlideByIndex(index)}
            onLetterByOrder={onLetterByOrder}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="drag-zone">
      <Card className="drag-zone__card" fluid>
        <CardContent className="content-header">
          <CardHeader className="drag-zone-bar">
            <div className="drag-zone-title">
              <Icon name="edit" />
              Contenido de sesión: {session?.title}
            </div>
            <div className="drag-zone-buttons">
              <Popup
                content="Guardar contenido sesión"
                trigger={
                  <Button
                    compact
                    color="green"
                    disabled={session.id !== undefined ? false : true}
                    icon="save"
                    onClick={onSaveSlidesSession}
                    loading={loading}
                  />
                }
              />
              <Popup
                content="Cerrar edición de sesión"
                trigger={
                  <Button
                    compact
                    color="red"
                    disabled={session.id !== undefined ? false : true}
                    icon="close"
                    onClick={onOpenCloseConfirm}
                  />
                }
              />
            </div>
          </CardHeader>
        </CardContent>
        <CardContent
          className="content-body"
          onDrop={onDropTemplate}
          onDragOver={onDragOver}
        >
          {session == null ? (
            <div className="drag-zone__not-session"></div>
          ) : size(slidesActiveSessionEdit) !== 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={slidesActiveSessionEdit.map((slide) => slide.id)}
                strategy={verticalListSortingStrategy}
              >
                <SlidesSessionContainer />
              </SortableContext>
            </DndContext>
          ) : (
            <div className="drag-zone__session-empty" />
          )}
        </CardContent>
      </Card>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={onCloseSession}
        content={`¿Deseas cerrar el editor de contenido de sesión? Se perderán los cambios no guardados.`}
        size="mini"
      />
    </div>
  );
}
